import { SID_RE, makeMessage, type CheckoutMessageType, type MessageData } from '../protocol';

/**
 * The frame's one-way line to the merchant page (embed.js). It is opened only
 * when the frame was loaded the way embed.js loads it, and only towards the
 * parent origin that both the fragment and the browser agree on. When any of
 * that fails, messaging is off but the checkout still works: the customer can
 * pay, they just get no overlay events.
 */
export interface ParentChannel {
  /** Loaded with ?embed=1: draw the compact in-overlay layout. */
  embedded: boolean;
  /** Messages will actually be sent. */
  enabled: boolean;
  sid: string | null;
  parentOrigin: string | null;
  post<T extends CheckoutMessageType>(type: T, data: MessageData<T>): void;
}

export interface ParentEnv {
  location: { search: string; hash: string; pathname: string; ancestorOrigins?: { length: number; [i: number]: string } };
  history: { replaceState(data: unknown, unused: string, url?: string): void };
  self: unknown;
  parent: { postMessage(message: unknown, targetOrigin: string): void } | null;
  referrer: string;
}

/** `p` must be a bare http(s) origin, exactly as `URL.origin` prints it. */
export function isValidParentOrigin(p: string | null): p is string {
  if (!p || p === 'null') return false;
  try {
    const u = new URL(p);
    return (u.protocol === 'http:' || u.protocol === 'https:') && u.origin === p;
  } catch {
    return false;
  }
}

/** Does the browser's own view of who framed us agree with the fragment? */
export function parentMatchesBrowser(parent: string, env: ParentEnv): boolean {
  const ancestors = env.location.ancestorOrigins;
  if (ancestors) return ancestors.length > 0 && ancestors[0] === parent;
  if (env.referrer) {
    try {
      return new URL(env.referrer).origin === parent;
    } catch {
      return false;
    }
  }
  return true;
}

export function createParentChannel(env: ParentEnv): ParentChannel {
  const embedded = new URLSearchParams(env.location.search).get('embed') === '1';
  const fragment = new URLSearchParams(env.location.hash.replace(/^#/, ''));
  const sidRaw = fragment.get('sid');
  const parentRaw = fragment.get('parent');

  // The sid is per-open state and has no business in the address bar, history
  // or a copied URL.
  if (env.location.hash) {
    try {
      env.history.replaceState(null, '', env.location.pathname + env.location.search);
    } catch {
      /* sandboxed history: the fragment just stays */
    }
  }

  const sid = sidRaw && SID_RE.test(sidRaw) ? sidRaw : null;
  const framed = env.parent !== null && env.parent !== env.self;
  const enabled =
    embedded && framed && sid !== null && isValidParentOrigin(parentRaw) && parentMatchesBrowser(parentRaw, env);
  const parentOrigin = enabled ? parentRaw : null;

  return {
    embedded,
    enabled,
    sid,
    parentOrigin,
    post(type, data) {
      if (!enabled || !sid || !parentOrigin || !env.parent) return;
      // Always an explicit target origin, never '*': a parent that navigated
      // away to another site must not receive the payment events.
      env.parent.postMessage(makeMessage(sid, type, data), parentOrigin);
    },
  };
}

export function browserParentEnv(): ParentEnv {
  return {
    location: window.location,
    history: window.history,
    self: window,
    parent: window.parent,
    referrer: document.referrer,
  };
}
